import { fetchAuthSession } from 'aws-amplify/auth';
import { Signer } from '@aws-amplify/core/internals/utils';
import { NORMALIZED_STAGE } from 'src/constants/EnvironmentConstants';
import { WORKDOCS_TOKEN_HEADER } from 'src/constants/WorkDocsConstants';

const WORKDOCS_API_BASE_BY_STAGE: Record<string, string> = {
  alpha: 'https://alpha.workdocs.finsuite.device.finance.amazon.dev',
  beta: 'https://beta.workdocs.finsuite.device.finance.amazon.dev',
  prod: 'https://workdocs.finsuite.device.finance.amazon.dev'
};

const WORKDOCS_APP_ID_BY_STAGE: Record<string, string> = {
  alpha: '31026e57-2f3e-415d-8494-afe5083c9ad7',
  beta: '766cf54b-d911-4524-9b79-895196547595',
  prod: '7dea905e-cb8b-441c-b43d-61d8b23bb266'
};

const WORKDOCS_API_BASE = WORKDOCS_API_BASE_BY_STAGE[NORMALIZED_STAGE] || WORKDOCS_API_BASE_BY_STAGE.alpha;

// Root WorkDocs folder: "Fintech Leadership Strategy Review" (contains year subfolders)
export const TECH_MBR_FOLDER_ID = '53a0043f87175d80d54e69318e664d9edca1f4907658675f76b377881de80492';

/**
 * Fixed WorkDocs folder the OE Interactive Report uploads review `.docx` files into when a report
 * moves Draft → In Review. A single shared destination (not a per-user tree), so every reviewer sees
 * the same "In Review" folder.
 */
export const OE_REVIEW_FOLDER_ID = 'e9787e98ae24ac20eaa1db65a3b3e6f27901f176bc80190a5ca2806c1f97cf3b';

/**
 * Fixed WorkDocs folder the OE Interactive Report copies the published `.docx` into when a report
 * moves In Review → Published. Separate from {@link OE_REVIEW_FOLDER_ID} so the published record
 * links to a stable copy in the "Published" folder, independent of the mutable review document.
 */
export const OE_PUBLISHED_FOLDER_ID = '9d176e442a2e340a3adc9abef8b6d5325e313b510c7f210fad9092bfb0f7c372';

/**
 * Builds the WorkDocs web URL for a document id, e.g.
 * `https://amazon.awsapps.com/workdocs-amazon/index.html#/document/{id}`. `uploadDocument` returns
 * only the id/version (not a web link), so callers that need an openable link — the OE report stores
 * one on the document as its "Open Document" link — construct it from the returned `documentId`.
 */
export const buildWorkDocsDocumentUrl = (documentId: string): string =>
  `https://amazon.awsapps.com/workdocs-amazon/index.html#/document/${documentId}`;

/**
 * The `#/document/{id}` hash route, with the id in group 1. The id is captured until the next `/`,
 * `?`, or end so trailing route segments or query params don't bleed into it.
 */
const WORKDOCS_DOCUMENT_HASH_RE = /#\/document\/([^/?#]+)/;

/**
 * Extracts the WorkDocs document id from a document web URL produced by {@link buildWorkDocsDocumentUrl}
 * (`.../index.html#/document/{id}`), or null when the string is not an https WorkDocs document URL
 * (e.g. a SharePoint link, a folder URL, or an unparseable value). Used on publish to find the
 * In Review document to copy; a null result means "no WorkDocs source to copy from".
 */
export const parseWorkDocsDocumentId = (url: string): string | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.hostname.toLowerCase() !== 'amazon.awsapps.com') return null;
  const match = parsed.hash.match(WORKDOCS_DOCUMENT_HASH_RE);
  return match ? match[1] : null;
};

/**
 * Builds the "open in the Office (SharePoint) editor" variant of a WorkDocs document URL. WorkDocs
 * opens the document straight in the Office Online editor in-place when the document hash route
 * carries `?officeEditor=true` (plus a `siteReturnPath` pointing back at the plain `#/document/{id}`
 * view so the editor's "back" returns there) — the same URL WorkDocs itself produces for "Edit in
 * SharePoint". It is derived purely from the document id we already store; no server lookup needed.
 *
 * Returns null when `workdocsUrl` is not a WorkDocs document URL (e.g. a SharePoint link or an
 * unparseable value), so callers can fall back to the stored link unchanged.
 */
export const buildWorkDocsOfficeEditorUrl = (workdocsUrl: string): string | null => {
  const id = parseWorkDocsDocumentId(workdocsUrl);
  if (!id) return null;
  const returnPath = encodeURIComponent(`#/document/${id}`);
  return `${buildWorkDocsDocumentUrl(id)}?officeEditor=true&siteReturnPath=${returnPath}`;
};

export const WORKDOCS_APP_ID = WORKDOCS_APP_ID_BY_STAGE[NORMALIZED_STAGE] || WORKDOCS_APP_ID_BY_STAGE.alpha;

export interface WorkDocsDocument {
  id: string;
  name: string;
  contentType: string;
  size: number;
  createdTimestamp: string;
  modifiedTimestamp: string;
  creatorId: string;
  latestVersionId: string;
}

export interface WorkDocsFolder {
  id: string;
  name: string;
  size: number;
  createdTimestamp: string;
  modifiedTimestamp: string;
  creatorId: string;
}

export interface FolderContentsResponse {
  documents: WorkDocsDocument[];
  folders: WorkDocsFolder[];
}

export interface CreateFolderResponse {
  folderId: string;
  name: string;
  parentFolderId: string;
}

export interface UploadDocumentResponse {
  documentId: string;
  versionId: string;
  name: string;
  parentFolderId: string;
}

export interface DeleteDocumentResponse {
  documentId: string;
  deleted: boolean;
}

interface ZocaloVersionMetadata {
  Name?: string;
  ContentType?: string;
  Size?: number;
  Id?: string;
}

interface ZocaloDocumentResponse {
  Id: string;
  LatestVersionMetadata?: ZocaloVersionMetadata;
  Created?: string;
  Modified?: string;
  CreatorId?: string;
}

interface ZocaloFolderResponse {
  Id: string;
  Name?: string;
  Size?: number;
  LatestVersionMetadata?: ZocaloVersionMetadata;
  Created?: string;
  Modified?: string;
  CreatorId?: string;
}

interface ZocaloFolderContentsResponse {
  Documents?: ZocaloDocumentResponse[];
  documents?: ZocaloDocumentResponse[];
  Folders?: ZocaloFolderResponse[];
  folders?: ZocaloFolderResponse[];
  Marker?: string;
}

export interface DownloadUrlResponse {
  documentId: string;
  versionId: string;
  name: string;
  contentType: string;
  size: number;
  downloadUrl: string;
  workdocsUrl: string;
}

export interface TokenResponse {
  // `accessToken` is itself a base64-encoded JSON descriptor that carries the
  // absolute `expiryTimeInMS` (and the nested WorkDocs JWT). Use getTokenExpiry
  // to read the expiry — it is NOT a top-level field on this response.
  accessToken: string;
  refreshToken: string;
  user?: { Id: string; RootFolderId: string };
}

/**
 * Extracts the absolute epoch-ms expiry from a WorkDocs access token. The token
 * the backend returns is a base64-encoded JSON descriptor whose `expiryTimeInMS`
 * is the single source of truth for expiry. Returns null if it can't be read.
 */
export function getTokenExpiry(accessToken: string): number | null {
  try {
    const decoded = JSON.parse(atob(accessToken)) as { expiryTimeInMS?: number };
    return typeof decoded.expiryTimeInMS === 'number' && decoded.expiryTimeInMS > 0 ? decoded.expiryTimeInMS : null;
  } catch {
    return null;
  }
}

async function signedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) throw new Error('No AWS credentials available');

  const extraHeaders = (options.headers as Record<string, string>) || {};

  const request = {
    method: options.method || 'GET',
    url,
    // Caller headers (e.g. the WorkDocs token) are signed: SigV4 requires all x-amz-*
    // headers be in SignedHeaders, so they must be present before Signer.sign.
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
    data: options.body as string | undefined
  };

  const signed = Signer.sign(
    request,
    {
      access_key: credentials.accessKeyId,
      secret_key: credentials.secretAccessKey,
      session_token: credentials.sessionToken || ''
    },
    { region: 'us-west-2', service: 'execute-api' }
  );

  return fetch(url, { ...options, headers: { ...signed.headers, ...extraHeaders } });
}

async function signedFetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const resp = await signedFetch(url, options);
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).detail || (err as Record<string, string>).error || `Request failed: ${resp.status}`);
  }
  return resp.json();
}

export async function exchangeAuthCode(authCode: string, codeVerifier: string): Promise<TokenResponse> {
  return signedFetchJson<TokenResponse>(`${WORKDOCS_API_BASE}/token`, {
    method: 'POST',
    body: JSON.stringify({ authCode, codeVerifier })
  });
}

export async function refreshAccessToken(accessToken: string, refreshToken: string): Promise<TokenResponse> {
  return signedFetchJson<TokenResponse>(`${WORKDOCS_API_BASE}/token/refresh`, {
    method: 'POST',
    body: JSON.stringify({ accessToken, refreshToken })
  });
}

export async function listFolderContents(folderId: string, accessToken: string): Promise<FolderContentsResponse> {
  const allDocuments: WorkDocsDocument[] = [];
  const allFolders: WorkDocsFolder[] = [];
  let marker: string | undefined;

  do {
    const url = `${WORKDOCS_API_BASE}/folders/${folderId}/contents${marker ? `?marker=${encodeURIComponent(marker)}` : ''}`;
    const data = await signedFetchJson<ZocaloFolderContentsResponse>(url, {
      method: 'GET',
      headers: { [WORKDOCS_TOKEN_HEADER]: accessToken }
    });

    allDocuments.push(
      ...(data.Documents || data.documents || []).map((doc: ZocaloDocumentResponse) => ({
        id: doc.Id,
        name: doc.LatestVersionMetadata?.Name || 'Unknown',
        contentType: doc.LatestVersionMetadata?.ContentType || '',
        size: doc.LatestVersionMetadata?.Size || 0,
        createdTimestamp: doc.Created || '',
        modifiedTimestamp: doc.Modified || '',
        creatorId: doc.CreatorId || '',
        latestVersionId: doc.LatestVersionMetadata?.Id || ''
      }))
    );

    allFolders.push(
      ...(data.Folders || data.folders || []).map((f: ZocaloFolderResponse) => ({
        id: f.Id,
        name: f.Name || 'Unknown',
        size: f.Size || f.LatestVersionMetadata?.Size || 0,
        createdTimestamp: f.Created || '',
        modifiedTimestamp: f.Modified || '',
        creatorId: f.CreatorId || ''
      }))
    );

    marker = data.Marker || undefined;
  } while (marker);

  return {
    documents: allDocuments,
    folders: allFolders
  };
}

export async function getDocumentDownloadUrl(documentId: string, accessToken: string): Promise<DownloadUrlResponse> {
  return signedFetchJson<DownloadUrlResponse>(`${WORKDOCS_API_BASE}/documents/${documentId}/download-url`, {
    method: 'GET',
    headers: { [WORKDOCS_TOKEN_HEADER]: accessToken }
  });
}

/**
 * Creates a single sub-folder named `name` under `parentFolderId`. The backend does
 * NOT de-duplicate by name — calling this twice makes two same-named folders — so
 * prefer {@link findOrCreateFoldersUnder} unless the parent is known to be empty.
 */
export async function createFolder(parentFolderId: string, name: string, accessToken: string): Promise<CreateFolderResponse> {
  return signedFetchJson<CreateFolderResponse>(`${WORKDOCS_API_BASE}/create-folder`, {
    method: 'POST',
    body: JSON.stringify({ parentFolderId, name }),
    headers: { [WORKDOCS_TOKEN_HEADER]: accessToken }
  });
}

/**
 * Uploads a document into a WorkDocs folder. `base64Content` is the raw file bytes
 * base64-encoded; the backend performs the Zocalo three-step upload (initiate ->
 * PUT to a presigned S3 URL -> mark the version ACTIVE) on the caller's behalf.
 *
 * The backend does NOT de-duplicate by name — uploading the same file name twice
 * creates two documents — so callers that want replace-semantics must delete first.
 */
export async function uploadDocument(
  parentFolderId: string,
  name: string,
  contentType: string,
  base64Content: string,
  accessToken: string
): Promise<UploadDocumentResponse> {
  return signedFetchJson<UploadDocumentResponse>(`${WORKDOCS_API_BASE}/documents`, {
    method: 'POST',
    body: JSON.stringify({ parentFolderId, name, contentType, content: base64Content }),
    headers: { [WORKDOCS_TOKEN_HEADER]: accessToken }
  });
}

/**
 * Deletes a document from WorkDocs by id. The backend proxies the Zocalo delete on the
 * caller's behalf, so a caller can only remove documents their access token is permitted to
 * delete. The backend normalizes WorkDocs' 204 No Content to `{ documentId, deleted: true }`.
 *
 * Idempotency is the backend's responsibility — a second delete of an already-removed id may
 * surface an error; callers refresh the folder listing on success either way.
 */
export async function deleteDocument(documentId: string, accessToken: string): Promise<DeleteDocumentResponse> {
  return signedFetchJson<DeleteDocumentResponse>(`${WORKDOCS_API_BASE}/documents/${documentId}`, {
    method: 'DELETE',
    headers: { [WORKDOCS_TOKEN_HEADER]: accessToken }
  });
}

/**
 * Idempotently ensures every name in `names` exists as a folder under
 * `parentFolderId`, returning a `name -> folderId` map. Lists the parent's
 * children ONCE, reuses any folder whose name matches exactly, and creates only
 * the missing ones (sequentially). This is the single idempotency choke point for
 * folder provisioning: a first run, a resume after a partial failure, and a
 * re-provision after an accidental deletion all flow through here and converge on
 * the same tree.
 */
export async function findOrCreateFoldersUnder(parentFolderId: string, names: string[], accessToken: string): Promise<Map<string, string>> {
  const { folders } = await listFolderContents(parentFolderId, accessToken);
  const existingByName = new Map(folders.map((folder) => [folder.name, folder.id]));

  const result = new Map<string, string>();
  for (const name of names) {
    const existingId = existingByName.get(name);
    if (existingId) {
      result.set(name, existingId);
      continue;
    }
    const created = await createFolder(parentFolderId, name, accessToken);
    result.set(name, created.folderId);
  }

  return result;
}

/** The role to grant a reviewer on the WorkDocs document. */
export type WorkDocsReviewerRole = 'VIEWER' | 'CONTRIBUTOR' | 'COOWNER';

/** Per-alias outcome returned by the share endpoint. */
export interface ShareResult {
  alias: string;
  status: 'SUCCESS' | 'FAILURE';
  message: string;
}

export interface ShareDocumentResponse {
  results: ShareResult[];
}

/**
 * Share a WorkDocs document with one or more Amazon users by alias.
 *
 * The backend resolves each alias to a WorkDocs user id, then calls
 * AddResourcePermissions. Partial failures are returned per-alias so
 * the caller can warn without blocking submission.
 *
 * At most 20 aliases per call.
 */
export async function shareDocument(
  documentId: string,
  aliases: string[],
  role: WorkDocsReviewerRole,
  accessToken: string
): Promise<ShareDocumentResponse> {
  return signedFetchJson<ShareDocumentResponse>(`${WORKDOCS_API_BASE}/documents/${documentId}/share`, {
    method: 'POST',
    body: JSON.stringify({ aliases, role }),
    headers: { [WORKDOCS_TOKEN_HEADER]: accessToken }
  });
}
